import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  TextField,
  Chip,
  IconButton,
  Paper,
  Fade,
} from '@mui/material';
import { Node } from 'reactflow';
import { WorkflowNodeData } from './WorkflowNode';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CodeIcon from '@mui/icons-material/Code';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

interface SmartConnectionDialogProps {
  open: boolean;
  onClose: () => void;
  sourceNodes: Node<WorkflowNodeData>[];
  targetNode: Node<WorkflowNodeData> | null;
  nodes: Node<WorkflowNodeData>[];
  onSave: (config: ConnectionConfig) => void;
}

export interface ConnectionConfig {
  inputMapping: Record<string, string>;
  promptUpdates?: string;
  conditionUpdates?: { branchId: string; condition: string }[];
  dataToDisplay?: Record<string, string>;
}

interface OutputField {
  name: string;
  type: string;
  description: string;
  jsonPath: string;
}

interface EditableField {
  name: string;
  type: 'prompt' | 'condition' | 'data_display' | 'data_to_display' | 'input_mapping';
  currentValue: string;
  placeholder: string;
}

export const SmartConnectionDialog: React.FC<SmartConnectionDialogProps> = ({
  open,
  onClose,
  sourceNodes,
  targetNode,
  nodes,
  onSave,
}) => {
  const [mappings, setMappings] = useState<Record<string, string>>({});
  const [promptText, setPromptText] = useState('');
  const [conditions, setConditions] = useState<{ branchId: string; condition: string }[]>([]);
  const [selectedSourceIds, setSelectedSourceIds] = useState<string[]>([]);
  const [dataToDisplay, setDataToDisplay] = useState<Record<string, string>>({});

  // Introspect source node outputs (handles multiple sources)
  const getSourceOutputs = (): OutputField[] => {
    if (sourceNodes.length === 0) return [];

    const allOutputs: OutputField[] = [];

    sourceNodes.forEach(sourceNode => {
      const stepId = sourceNode.id;
      const nodeType = sourceNode.data.type;

      const nodeLabel = sourceNode.data.label || stepId;

      switch (nodeType) {
        case 'agent':
        case 'tool':
          allOutputs.push({
            name: `${stepId}_output`,
            type: 'string',
            description: `Full response from ${nodeLabel}`,
            jsonPath: `$.steps.${stepId}.output`,
          });
          break;

        case 'human_input':
          const inputFields = sourceNode.data.rawData?.input_fields || sourceNode.data.config?.input_fields || [];
          inputFields.forEach((field: any) => {
            allOutputs.push({
              name: `${stepId}_${field.name}`,
              type: field.type,
              description: `User input from ${nodeLabel}: ${field.label || field.name}`,
              jsonPath: `$.steps.${stepId}.output.${field.name}`,
            });
          });
          break;

        case 'conditional':
          allOutputs.push({
            name: `${stepId}_decision`,
            type: 'string',
            description: `Selected branch from ${nodeLabel}`,
            jsonPath: `$.steps.${stepId}.output`,
          });
          break;

        case 'merge':
          allOutputs.push({
            name: `${stepId}_merged`,
            type: 'object',
            description: `Combined outputs from ${nodeLabel}`,
            jsonPath: `$.steps.${stepId}.output`,
          });
          break;

        case 'loop':
          allOutputs.push({
            name: `${stepId}_iterations`,
            type: 'array',
            description: `Results from ${nodeLabel} iterations`,
            jsonPath: `$.steps.${stepId}.output`,
          });
          break;

        default:
          allOutputs.push({
            name: `${stepId}_output`,
            type: 'any',
            description: `Output from ${nodeLabel}`,
            jsonPath: `$.steps.${stepId}.output`,
          });
          break;
      }
    });

    return allOutputs;
  };

  // Get editable fields in target node
  const getTargetFields = (): EditableField[] => {
    if (!targetNode) return [];

    const nodeType = targetNode.data.type;
    const fields: EditableField[] = [];

    switch (nodeType) {
      case 'agent':
        fields.push({
          name: 'prompt',
          type: 'prompt',
          currentValue: targetNode.data.rawData?.prompt || targetNode.data.config?.prompt || '',
          placeholder: 'Edit agent prompt here. Use {{variable}} for data insertion.',
        });
        break;

      case 'conditional':
        const routingConfig = targetNode.data.rawData?.routing_config || targetNode.data.config?.routing_config;
        if (routingConfig && routingConfig.type === 'expression') {
          (routingConfig.branches || []).forEach((branch: any, idx: number) => {
            fields.push({
              name: `Branch: ${branch.id || idx}`,
              type: 'condition',
              currentValue: branch.condition || '',
              placeholder: `e.g. decision == 'approve'`,
            });
          });
        } else {
          // No branches yet - show helper text
          fields.push({
            name: 'Branches',
            type: 'data_display',
            currentValue: 'No branches configured yet. Edit this node in the sidebar to add branches.',
            placeholder: '',
          });
        }
        break;

      case 'human_input':
        fields.push({
          name: 'data_to_display',
          type: 'data_to_display',
          currentValue: JSON.stringify(targetNode.data.rawData?.data_to_display || {}, null, 2),
          placeholder: 'Data to show to the reviewer',
        });
        break;

      case 'tool':
        fields.push({
          name: 'input_mapping',
          type: 'input_mapping',
          currentValue: JSON.stringify(targetNode.data.rawData?.input_mapping || {}, null, 2),
          placeholder: 'Map inputs to tool parameters',
        });
        break;
    }

    return fields;
  };

  const sourceOutputs = getSourceOutputs();
  const targetFields = getTargetFields();

  // Auto-suggest mappings
  useEffect(() => {
    if (sourceNodes.length === 0 || !targetNode) return;

    const suggested: Record<string, string> = {};

    // Recalculate sourceOutputs inside the effect to avoid stale closure
    const currentOutputs = getSourceOutputs();

    // Smart suggestions based on node types
    if (targetNode.data.type === 'agent') {
      const prompt = targetNode.data.rawData?.prompt || targetNode.data.config?.prompt || '';
      // Extract {{variables}} from prompt
      const variables = [...prompt.matchAll(/\{\{(\w+)\}\}/g)].map(m => m[1]);

      variables.forEach(varName => {
        const matchingOutput = currentOutputs.find(o =>
          o.name.toLowerCase().includes(varName.toLowerCase()) ||
          varName.toLowerCase().includes(o.name.toLowerCase())
        );
        if (matchingOutput) {
          suggested[varName] = matchingOutput.jsonPath;
        }
      });
    }

    // Suggest decision field for conditionals if any source is human_input
    if (targetNode.data.type === 'conditional') {
      const hasHumanInput = sourceNodes.some(node => node.data.type === 'human_input');
      if (hasHumanInput) {
        const decisionField = currentOutputs.find(o => o.name.includes('decision'));
        if (decisionField) {
          suggested['decision'] = decisionField.jsonPath;
        }
      }
    }

    setMappings(suggested);
  }, [sourceNodes, targetNode]);

  useEffect(() => {
    if (targetNode?.data.type === 'agent') {
      setPromptText(targetNode.data.rawData?.prompt || targetNode.data.config?.prompt || '');
    }
    if (targetNode?.data.type === 'conditional') {
      const branches = targetNode.data.rawData?.routing_config?.branches || [];
      setConditions(branches.map((b: any) => ({ branchId: b.id, condition: b.condition || '' })));
    }
    if (targetNode?.data.type === 'human_input') {
      const data = targetNode.data.rawData?.data_to_display || targetNode.data.config?.data_to_display || {};
      setDataToDisplay(data);
    }
  }, [targetNode]);

  const handleSave = () => {
    const config: ConnectionConfig = {
      inputMapping: mappings,
    };

    if (targetNode?.data.type === 'agent') {
      const originalPrompt = targetNode.data.rawData?.prompt || targetNode.data.config?.prompt || '';
      if (promptText !== originalPrompt) {
        config.promptUpdates = promptText;
      }
    }

    if (targetNode?.data.type === 'conditional' && conditions.length > 0) {
      config.conditionUpdates = conditions;
    }

    if (targetNode?.data.type === 'human_input' && Object.keys(dataToDisplay).length > 0) {
      config.dataToDisplay = dataToDisplay;
    }

    onSave(config);
    onClose();
  };

  if (sourceNodes.length === 0 || !targetNode) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          bgcolor: '#F8FAFC',
          border: '1px solid #E2E8F0',
          minHeight: '600px',
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* HEADER */}
        <Box sx={{
          px: 3,
          py: 2,
          bgcolor: '#FFFFFF',
          borderBottom: '1px solid #E2E8F0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: 32,
              height: 32,
              borderRadius: '8px',
              bgcolor: '#B87333',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <AutoFixHighIcon sx={{ fontSize: 18, color: '#FFFFFF' }} />
            </Box>
            <Typography sx={{
              fontWeight: 800,
              fontSize: '1.1rem',
              color: '#1E293B',
              fontFamily: '"IBM Plex Mono", monospace',
            }}>
              Smart Connection
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        {/* MAIN CONTENT: 3-COLUMN LAYOUT */}
        <Box sx={{ display: 'flex', height: '500px' }}>
          {/* LEFT: SOURCE NODE OUTPUTS */}
          <Box sx={{
            flex: 1,
            p: 3,
            borderRight: '1px solid #E2E8F0',
            bgcolor: '#FFFFFF',
            overflowY: 'auto',
          }}>
            <Typography sx={{
              fontSize: '11px',
              fontWeight: 800,
              color: '#64748B',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              mb: 2,
            }}>
              From: {sourceNodes.length === 1
                ? sourceNodes[0].data.label
                : `${sourceNodes.length} nodes (${sourceNodes.map(n => n.data.label).join(', ')})`}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {sourceOutputs.map((output) => (
                <Fade in key={output.name} timeout={300}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: '8px',
                      border: '1px solid #E2E8F0',
                      bgcolor: '#F8FAFC',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: '#B87333',
                        bgcolor: '#FFF7F0',
                        transform: 'translateX(4px)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <CodeIcon sx={{ fontSize: 14, color: '#B87333' }} />
                      <Typography sx={{
                        fontWeight: 700,
                        fontSize: '13px',
                        fontFamily: '"IBM Plex Mono", monospace',
                        color: '#1E293B',
                      }}>
                        {output.name}
                      </Typography>
                      <Chip
                        label={output.type}
                        size="small"
                        sx={{
                          height: '18px',
                          fontSize: '10px',
                          fontWeight: 700,
                          bgcolor: '#E0F2FE',
                          color: '#0369A1',
                        }}
                      />
                    </Box>
                    <Typography sx={{
                      fontSize: '11px',
                      color: '#64748B',
                      fontFamily: 'Inter, sans-serif',
                      mb: 1,
                    }}>
                      {output.description}
                    </Typography>
                    <Box sx={{
                      p: 1,
                      borderRadius: '4px',
                      bgcolor: '#F1F5F9',
                      border: '1px solid #E2E8F0',
                    }}>
                      <Typography sx={{
                        fontSize: '10px',
                        fontFamily: '"IBM Plex Mono", monospace',
                        color: '#475569',
                      }}>
                        {output.jsonPath}
                      </Typography>
                    </Box>
                  </Paper>
                </Fade>
              ))}
            </Box>
          </Box>

          {/* MIDDLE: CONNECTOR ARROW */}
          <Box sx={{
            width: '80px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#FAFAFA',
            borderRight: '1px solid #E2E8F0',
          }}>
            <ArrowForwardIcon sx={{ fontSize: 40, color: '#B87333', opacity: 0.3 }} />
          </Box>

          {/* RIGHT: TARGET NODE FIELDS */}
          <Box sx={{
            flex: 1,
            p: 3,
            bgcolor: '#FFFFFF',
            overflowY: 'auto',
          }}>
            <Typography sx={{
              fontSize: '11px',
              fontWeight: 800,
              color: '#64748B',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              mb: 2,
            }}>
              To: {targetNode.data.label}
            </Typography>

            {/* EDITABLE FIELDS */}
            {targetFields.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {targetFields.map((field, idx) => (
                  <Fade in key={field.name} timeout={400 + idx * 100}>
                    <Box>
                      <Typography sx={{
                        fontSize: '11px',
                        fontWeight: 700,
                        color: '#475569',
                        mb: 1,
                      }}>
                        {field.name}
                      </Typography>
                      {field.type === 'prompt' ? (
                        <TextField
                          fullWidth
                          multiline
                          rows={6}
                          value={promptText}
                          onChange={(e) => setPromptText(e.target.value)}
                          placeholder={field.placeholder}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              fontFamily: '"IBM Plex Mono", monospace',
                              fontSize: '12px',
                              bgcolor: '#F8FAFC',
                              borderRadius: '8px',
                            },
                          }}
                        />
                      ) : field.type === 'condition' ? (
                        <TextField
                          fullWidth
                          value={conditions[idx]?.condition || ''}
                          onChange={(e) => {
                            const updated = [...conditions];
                            updated[idx] = { ...updated[idx], condition: e.target.value };
                            setConditions(updated);
                          }}
                          placeholder={field.placeholder}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              fontFamily: '"IBM Plex Mono", monospace',
                              fontSize: '12px',
                              bgcolor: '#F8FAFC',
                              borderRadius: '8px',
                            },
                          }}
                        />
                      ) : field.type === 'data_to_display' ? (
                        <Box>
                          <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                            <Button
                              size="small"
                              onClick={() => {
                                const newKey = `field_${Object.keys(dataToDisplay).length + 1}`;
                                setDataToDisplay({ ...dataToDisplay, [newKey]: '' });
                              }}
                              sx={{ textTransform: 'none', fontSize: '11px', fontWeight: 700 }}
                            >
                              + Add Display Field
                            </Button>
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {Object.entries(dataToDisplay).map(([key, value]) => (
                              <Paper key={key} elevation={0} sx={{ p: 1.5, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '6px' }}>
                                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="Field Name"
                                    value={key}
                                    onChange={(e) => {
                                      const oldKey = key;
                                      const newKey = e.target.value;
                                      const newData = { ...dataToDisplay };
                                      delete newData[oldKey];
                                      newData[newKey] = value;
                                      setDataToDisplay(newData);
                                    }}
                                    sx={{ '& .MuiOutlinedInput-root': { fontSize: '12px', bgcolor: '#FFFFFF' } }}
                                    placeholder="e.g. campaign_plan"
                                  />
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      const newData = { ...dataToDisplay };
                                      delete newData[key];
                                      setDataToDisplay(newData);
                                    }}
                                    sx={{ color: '#EF4444' }}
                                  >
                                    <CloseIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Box>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="JSONPath Expression"
                                  value={value}
                                  onChange={(e) => {
                                    setDataToDisplay({ ...dataToDisplay, [key]: e.target.value });
                                  }}
                                  sx={{ '& .MuiOutlinedInput-root': { fontSize: '12px', bgcolor: '#FFFFFF', fontFamily: '"IBM Plex Mono", monospace' } }}
                                  placeholder="$.steps.[step_id].output"
                                />
                              </Paper>
                            ))}
                            {Object.keys(dataToDisplay).length === 0 && (
                              <Typography sx={{ fontSize: '12px', color: '#94A3B8', fontStyle: 'italic', textAlign: 'center', py: 1 }}>
                                Click "+ Add Display Field" to show data to the reviewer
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      ) : (
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          value={field.currentValue}
                          placeholder={field.placeholder}
                          disabled
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              fontFamily: '"IBM Plex Mono", monospace',
                              fontSize: '11px',
                              bgcolor: '#F1F5F9',
                              borderRadius: '8px',
                            },
                          }}
                        />
                      )}
                    </Box>
                  </Fade>
                ))}
              </Box>
            ) : (
              <Typography sx={{
                fontSize: '12px',
                color: '#94A3B8',
                fontStyle: 'italic',
              }}>
                No editable fields for this node type.
              </Typography>
            )}

            {/* SUGGESTED MAPPINGS */}
            {Object.keys(mappings).length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography sx={{
                  fontSize: '11px',
                  fontWeight: 700,
                  color: '#475569',
                  mb: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}>
                  <CheckCircleIcon sx={{ fontSize: 14, color: '#10B981' }} />
                  Auto-detected Mappings
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {Object.entries(mappings).map(([key, value]) => (
                    <Paper
                      key={key}
                      elevation={0}
                      sx={{
                        p: 1.5,
                        borderRadius: '6px',
                        bgcolor: '#ECFDF5',
                        border: '1px solid #A7F3D0',
                      }}
                    >
                      <Typography sx={{
                        fontSize: '11px',
                        fontFamily: '"IBM Plex Mono", monospace',
                        color: '#065F46',
                      }}>
                        <span style={{ fontWeight: 700 }}>{key}</span> ← {value}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        {/* FOOTER */}
        <Box sx={{
          px: 3,
          py: 2,
          bgcolor: '#FFFFFF',
          borderTop: '1px solid #E2E8F0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <Typography sx={{ fontSize: '11px', color: '#64748B', fontStyle: 'italic' }}>
            {Object.keys(mappings).length} mapping{Object.keys(mappings).length !== 1 ? 's' : ''} detected
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              onClick={onClose}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '13px',
                color: '#64748B',
                borderRadius: '8px',
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disableElevation
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '13px',
                bgcolor: '#B87333',
                borderRadius: '8px',
                px: 3,
                '&:hover': {
                  bgcolor: '#A66429',
                },
              }}
            >
              Save Connection
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
